/**
 * The photo scrapbook (new on the remodelled landing page, landing.V2): the
 * gardener's photos are buried in a camera roll, and Grow files each one on
 * its plant by date. The evidence is a story in three steps: a "Photos"
 * window holding the owner's real photo search (an endless grid of near
 * duplicates), the line "Grow files each photo on its plant", and a phone
 * holding the owner's real Plants screen (public/landing/photos-mess.jpg and
 * plants-ordered.png).
 *
 * The icon is a hand-drawn messy stack of photos. The sentence about Grow
 * More suggesting the plant shows only on a wide screen, where it fits.
 *
 * Words (en/es): landing.v2.scrapbook.*.
 *
 * @module Scrapbook
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FeatureBlock } from '../components/FeatureBlock';
import { FlowArrow } from '../components/FlowArrow';
import { PhoneFrame } from '../components/PhoneFrame';

/**
 * A messy stack of three photos, drawn by hand: the section's icon. Hidden
 * from screen readers (the frame hides its icon).
 */
const MessyPhotosIcon: React.FC = () => (
  <svg viewBox="0 0 40 40" className="h-9 w-9" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="9" width="17" height="14" rx="2" transform="rotate(-14 12 16)" />
    <rect x="18" y="5" width="16" height="13" rx="2" transform="rotate(11 26 11)" />
    <rect x="10" y="19" width="19" height="15" rx="2" transform="rotate(-4 19 26)" fill="#E8EDE7" />
    <path d="M13 31l4-5 3 3 3-4 4 6" />
    <circle cx="15" cy="24" r="1.4" />
  </svg>
);

/** The photo scrapbook section. */
export const Scrapbook: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  return (
    <FeatureBlock
      icon={<MessyPhotosIcon />}
      quote
      pain={t('landing.v2.scrapbook.pain')}
      title={t('landing.v2.scrapbook.title')}
      line={t('landing.v2.scrapbook.body')}
      more={t('landing.v2.scrapbook.more')}
      second={t('landing.v2.scrapbook.second')}
      evidence={(
        <>
          {/* A desktop photo window: three dots, its name, and the owner's real search inside. */}
          <div className="overflow-hidden rounded-xl border border-[#D5D3CC] bg-white shadow-[0_18px_36px_-14px_rgb(0_0_0/0.35)]">
            <div className="flex items-center gap-1.5 border-b border-[#D5D3CC] bg-[#F3EFE8] px-3 py-2 font-sans text-xs font-semibold leading-none text-grow-charcoal">
              <span className="h-2.5 w-2.5 rounded-full bg-[#D5D3CC]" aria-hidden="true" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#D5D3CC]" aria-hidden="true" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#D5D3CC]" aria-hidden="true" />
              <b className="ml-1.5">{t('landing.v2.scrapbook.windowTitle')}</b>
            </div>
            <img src="/landing/photos-mess.jpg" alt={t('landing.v2.scrapbook.messAlt')} loading="lazy" className="block w-full" />
          </div>
          <FlowArrow>{t('landing.v2.scrapbook.reads')}</FlowArrow>
          <PhoneFrame>
            <img src="/landing/plants-ordered.png" alt={t('landing.v2.scrapbook.plantsAlt')} loading="lazy" className="block w-full" />
          </PhoneFrame>
        </>
      )}
    />
  );
};
